import { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { globalEnv } from "~/global-env";
import {
  useGetCaseProfilesForScreeningQuery,
  useGetPicrPositionDetailsQuery,
} from "~/graphql/supergraphHooks";
import { usePicrRevampFeatureFlag } from "~/pages/Compliance/flags";
import { ProfileWithScreening } from "../../types";
import { ViewScreeningDetailsFullscreenTakeover } from "../index";

interface WrapperProps {
  caseId: number;
  readonly: boolean;
}

export const PicrScreeningWrapper = ({ caseId, readonly }: WrapperProps) => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const profileId = searchParams.get("profileId") || "";
  const profileIdInt = parseInt(profileId);
  const isPicrRevampEnabled = usePicrRevampFeatureFlag();

  const caseProfilesQuery = useGetCaseProfilesForScreeningQuery({
    variables: {
      caseId,
    },
  });

  const profiles = caseProfilesQuery.data?.complianceCase?.profiles ?? [];
  const isNewlyCreatedProfile = profiles.find((profile) => profile?.id === profileIdInt);

  const picrPositionDetailsQuery = useGetPicrPositionDetailsQuery({
    variables: {
      caseId: caseId,
      includeScreeningMatches: true,
    },
    fetchPolicy: "network-only",
  });

  const matchedPosition = isNewlyCreatedProfile
    ? undefined
    : picrPositionDetailsQuery.data?.picrPositionDetailsByCaseId.find((obj) => {
        const onboardingCase = obj.linkedOnboardingCase;
        return onboardingCase?.investingProfile?.id === profileIdInt
          ? onboardingCase.investingProfile
          : onboardingCase?.profiles?.find((p) => p?.id === profileIdInt);
      });
  const matchedPositionId = matchedPosition?.positionId;
  const matchedProfile = matchedPosition
    ? matchedPosition.linkedOnboardingCase?.investingProfile?.id === profileIdInt
      ? matchedPosition.linkedOnboardingCase.investingProfile
      : matchedPosition.linkedOnboardingCase?.profiles?.find((p) => p?.id === profileIdInt)
    : undefined;

  const positionDetails = picrPositionDetailsQuery.data?.picrPositionDetailsByCaseId?.find(
    (item) => String(item.linkedProfileId) === profileId,
  );

  const currentSelectedProfile = useMemo((): ProfileWithScreening | undefined => {
    // For PICR revamp, handle newly created and matched profiles first
    if (isPicrRevampEnabled) {
      if (isNewlyCreatedProfile) {
        return {
          ...isNewlyCreatedProfile,
          positionId: matchedPositionId,
        } as ProfileWithScreening;
      } else if (matchedProfile) {
        return {
          ...matchedProfile,
          positionId: matchedPositionId,
        } as ProfileWithScreening;
      }
      return undefined;
    }

    // Legacy path requires positionDetails
    if (!positionDetails) return undefined;

    const dateOfBirth = (positionDetails.linkedProfile?.dateOfBirth as string) ?? null;
    const dateOfOrganization =
      (positionDetails.linkedProfile?.dateOfOrganization as string) ?? null;

    return {
      id: positionDetails.linkedProfileId,
      positionId: positionDetails.positionId,
      ...positionDetails.linkedProfile,
      dateOfBirth,
      dateOfOrganization,
    } as ProfileWithScreening;
  }, [
    isNewlyCreatedProfile,
    isPicrRevampEnabled,
    matchedPositionId,
    matchedProfile,
    positionDetails,
  ]);

  const onExit = () => {
    history.push(`${globalEnv.complianceRoute}/case/${caseId}/payment-instruction-change`);
  };

  if (!profileId) {
    onExit();
  }
  return (
    <ViewScreeningDetailsFullscreenTakeover
      readonly={readonly}
      currentSelectedProfile={currentSelectedProfile}
      onExit={onExit}
      queryResults={[picrPositionDetailsQuery, caseProfilesQuery]}
      isPolling={false}
      riskAssessmentFailed={false}
      caseStatus={caseProfilesQuery.data?.complianceCase?.status}
    />
  );
};
